class ProductDetailSticky extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.scrollThreshold = this.getAttribute("scroll-threshold") || 300;
    this.fadeInDuration = 300;
  }

  connectedCallback() {
    this.setupScrollListener();
    this.setupInitialState();
    this.setupPriceSync();
  }

  disconnectedCallback() {
    this.removeScrollListener();
    this.teardownPriceSync();
  }

  setupInitialState() {
    this.style.opacity = "0";
    this.style.transition = `opacity ${this.fadeInDuration}ms ease-in-out`;
    this.style.visibility = "hidden";
  }

  setupScrollListener() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  removeScrollListener() {
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.scrollThreshold && !this.isVisible) {
      this.fadeIn();
    } else if (scrollTop <= this.scrollThreshold && this.isVisible) {
      this.fadeOut();
    }
  }

  fadeIn() {
    this.style.opacity = "1";
    this.isVisible = true;
    this.style.visibility = "visible";
    // Mirror the .is-active pattern used by other sticky bars in the theme
    // (blocks/sticky-container.liquid). The WhatsApp float section relies on
    // this class to detect visibility and align itself against the PDP bar.
    this.classList.add("is-active");
  }

  fadeOut() {
    this.style.opacity = "0";
    this.isVisible = false;
    this.style.visibility = "hidden";
    this.classList.remove("is-active");
  }

  /* ------------------------------------------------------------------------
     PRICE TOTAL
     Keeps the sticky bar's displayed price in sync with the upsells the
     customer has selected — including the variant chosen for each upsell.
     Base price comes from data-base-price (the main product's selected
     variant, refreshed by Liquid on every section re-render); each selected
     upsell's price comes from data-current-variant-price on its
     [data-upsell-matcher] item (kept current by upsell-variant-selector.js).
     ------------------------------------------------------------------------ */
  setupPriceSync() {
    this.productId = this.getAttribute("data-product-id");
    this.basePrice = parseInt(this.getAttribute("data-base-price"), 10);
    this.baseCompareAtPrice =
      parseInt(this.getAttribute("data-base-compare-at-price"), 10) || 0;
    this.moneyFormat =
      this.getAttribute("data-money-format") ||
      (window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.moneyFormat) ||
      "";
    this._recalcRaf = null;

    if (isNaN(this.basePrice)) return; // nothing to total against

    this.onUpsellChange = (event) => {
      if (
        event.type === "upsell:variant-changed" ||
        (event.target &&
          typeof event.target.matches === "function" &&
          event.target.matches('input[name="upsell"]'))
      ) {
        this.scheduleRecalc();
      }
    };
    this.onVariantRerendered = () => this.scheduleRecalc();

    document.addEventListener("change", this.onUpsellChange);
    document.addEventListener("upsell:variant-changed", this.onUpsellChange);
    document.addEventListener("variant:rerendered", this.onVariantRerendered);

    // Defer the first paint one frame so the upsell block has hydrated and
    // (after a section re-render) the preserved <kc-upsell> has been restored.
    this.scheduleRecalc();
  }

  teardownPriceSync() {
    if (this.onUpsellChange) {
      document.removeEventListener("change", this.onUpsellChange);
      document.removeEventListener(
        "upsell:variant-changed",
        this.onUpsellChange
      );
    }
    if (this.onVariantRerendered) {
      document.removeEventListener(
        "variant:rerendered",
        this.onVariantRerendered
      );
    }
    if (this._recalcRaf) {
      cancelAnimationFrame(this._recalcRaf);
      this._recalcRaf = null;
    }
  }

  scheduleRecalc() {
    if (this._recalcRaf) return;
    this._recalcRaf = requestAnimationFrame(() => {
      this._recalcRaf = null;
      this.recalcTotal();
    });
  }

  /**
   * Upsell items currently selected for this product. Mirrors upsell.js
   * handleFormSubmit: an item counts only if it is visible (not hidden by the
   * product-limit CSS) and checked in every kc-upsell block it appears in
   * (AND across blocks). Deduped by product key. Returns [data-upsell-matcher]
   * containers.
   */
  collectSelectedUpsells() {
    const roots = document.querySelectorAll(
      `kc-upsell[data-product-id="${this.productId}"]`
    );
    const stateByKey = new Map();

    roots.forEach((root) => {
      root.querySelectorAll('input[name="upsell"]').forEach((checkbox) => {
        const item = checkbox.closest("[data-upsell-matcher]");
        if (!item) return;
        if (getComputedStyle(item).display === "none") return;

        const key =
          checkbox.dataset.upsellProductKey || checkbox.dataset.index;
        if (!key) return;

        const existing = stateByKey.get(key);
        if (!existing) {
          stateByKey.set(key, { allChecked: checkbox.checked, item });
        } else {
          existing.allChecked = existing.allChecked && checkbox.checked;
        }
      });
    });

    const selected = [];
    stateByKey.forEach((entry) => {
      if (entry.allChecked) selected.push(entry.item);
    });
    return selected;
  }

  recalcTotal() {
    if (!this.priceListEl || !this.priceListEl.isConnected) {
      this.priceListEl = this.querySelector(".price-list");
    }
    if (!this.priceListEl || isNaN(this.basePrice)) return;

    let total = this.basePrice;
    let totalCompare =
      this.baseCompareAtPrice > this.basePrice
        ? this.baseCompareAtPrice
        : this.basePrice;

    this.collectSelectedUpsells().forEach((item) => {
      const price = parseInt(item.dataset.currentVariantPrice, 10);
      if (isNaN(price)) return; // can't price this item — skip rather than guess
      const compareAt =
        parseInt(item.dataset.currentVariantCompareAtPrice, 10) || 0;
      total += price;
      totalCompare += compareAt > price ? compareAt : price;
    });

    this.renderPrice(total, totalCompare);
  }

  renderPrice(total, totalCompare) {
    const onSale = totalCompare > total;
    const saleClass = onSale
      ? "text-on-sale price-regular-weight"
      : "text-subdued price-regular-weight";

    let html = `<sale-price class="${saleClass}">${this.formatMoney(
      total
    )}</sale-price>`;

    if (onSale) {
      html += `<compare-at-price class="text-subdued line-through price-regular-weight">${this.formatMoney(
        totalCompare
      )}</compare-at-price>`;
    }

    this.priceListEl.innerHTML = html;
  }

  /**
   * Format a price in cents using the shop's money format. Mirrors the
   * formatter in bundle-summary.js (DE/US/FR/CH thousand separators) and
   * strips a trailing ",00"/".00" to match price-list.liquid.
   */
  formatMoney(priceInCents) {
    if (typeof priceInCents !== "number" || isNaN(priceInCents)) return "";

    const amount = priceInCents / 100;
    const amountWithDot = amount.toFixed(2);
    const amountWithComma = amountWithDot.replace(".", ",");
    const amountNoDecimals = Math.floor(amount).toString();

    let formatted;
    if (this.moneyFormat) {
      const amountWithCommaThousands = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );
      const amountWithDotThousands = amountWithComma.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "."
      );
      const amountWithSpaceThousands = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        " "
      );
      const amountWithApostrophe = amountWithDot.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "'"
      );

      formatted = this.moneyFormat
        .replace("{{amount_with_comma_separator}}", amountWithDotThousands)
        .replace(
          "{{amount_no_decimals_with_comma_separator}}",
          amountNoDecimals
        )
        .replace("{{amount_with_space_separator}}", amountWithSpaceThousands)
        .replace("{{amount_with_apostrophe_separator}}", amountWithApostrophe)
        .replace("{{amount_no_decimals}}", amountNoDecimals)
        .replace("{{amount}}", amountWithCommaThousands);
    } else {
      formatted = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    }

    // price-list.liquid strips zero cents from the displayed price; match it,
    // but only on the cents group (not a ".00" inside a thousands-separated
    // integer like "1.001").
    return formatted.replace(/([.,])00(?=\D|$)/, "");
  }
}

if (!customElements.get("product-detail-sticky")) {
  customElements.define("product-detail-sticky", ProductDetailSticky);
}
